import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "../../lib/useReducedMotion.js";
import styles from "./LoopVideo.module.css";

/**
 * A silent product loop in a rounded frame, with a caption chip over the footage. It plays
 * only while `open` (e.g. its tab is showing) and on screen, and has its own play/pause
 * button (WCAG 2.2.2). Under reduced motion it waits on the poster until the visitor
 * presses play. `ratio` sets the frame; `focus` is where the frame crops a wider clip.
 */
export default function LoopVideo({
  src,
  poster,
  caption,
  focus = "center",
  ratio = "16 / 10",
  open = true,
  className = "",
}) {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  // null until the visitor presses the button; until then, play unless motion is reduced.
  const [choice, setChoice] = useState(null);
  const playing = choice ?? !reduced;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (!open || !playing) {
      el.pause();
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? el.play().catch(() => {}) : el.pause()),
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      el.pause();
    };
  }, [open, playing]);

  return (
    <figure className={`${styles.media} ${className}`.trim()} style={{ aspectRatio: ratio }}>
      <video
        ref={ref}
        className={styles.video}
        // The frame crops the wide clip's sides; `focus` keeps what matters in view.
        style={{ objectPosition: focus }}
        muted
        loop
        playsInline
        disablePictureInPicture
        preload="none"
        poster={poster}
        aria-hidden="true"
      >
        <source src={src} type="video/mp4" />
      </video>
      <figcaption className={styles.caption}>{caption}</figcaption>
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setChoice(!playing)}
        aria-label={playing ? "Pause video" : "Play video"}
      >
        <span className={playing ? styles.pauseIcon : styles.playIcon} aria-hidden="true" />
      </button>
    </figure>
  );
}
